import { BaiduMap, ClusterBubble, FullScreenControl, Link, Icon } from '@marriott/mi-ui-library';
import { useRef, useState } from 'react';
import { formatExitURL, formBaiduClusters } from '../HotelDirectoryMap/HotelDirectoryMapHelper';
import { CustomOverlay, MapRefs } from '@uiw/react-baidu-map';
import { BaiduClusterItem, InfoWindowDataList, MapContainerProps } from '../HotelDirectoryMap/HotelDirectoryMap.types';
import clsx from 'clsx';
import {
  CloseInfoWindowButton,
  InfoWindowContainer,
  InfoWindowRegionList,
} from '../HotelDirectoryMap/HotelDirectoryMap.styles';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { DEFAULT_ZOOM_LEVEL } from '../../../modules/constants/HotelDirectory.constants';

const BaiduMapClusters = ({
  center,
  markers,
  mapHeight,
  zoom,
  isMapLoaded,
  setIsMapLoaded,
  selectedRegion,
  isFiltersApplied,
  openInfoWindow,
  infoWindowData,
  setCenter,
  setOpenInfoWindow,
  setInfoWindowData,
  onZoomChange,
}: MapContainerProps) => {
  const { envVarsObject } = useClientEnvVarsStore();
  const BAIDU_MAP_API_KEY = envVarsObject?.['BAIDU_MAP_API_KEY'] || '';

  const container = useRef<HTMLDivElement>(null);
  const mapType = useRef<string>('');
  const [mapRef, setMapRef] = useState<MapRefs | undefined>();
  const [markerRegionData, setMarkerRegionData] = useState<InfoWindowDataList>();

  const onClusterClick = (cluster: BaiduClusterItem) => {
    const markerPosition = { lat: cluster?.latitude, lng: cluster?.longitude };
    setCenter(markerPosition);
    setInfoWindowData({ ...markerPosition, subRegions: cluster?.subRegions });
    setMarkerRegionData(cluster?.subRegions);
    setOpenInfoWindow(true);
  };

  const onMarkerClick = ({ code, label }: { code: string; label: string }) => {
    const url = formatExitURL(code, label, selectedRegion.code, isFiltersApplied);
    window.location.href = url;
  };

  const onZoom = ({ target }: { target: { getZoom: () => void } }) => {
    const newZoom = target?.getZoom?.();
    if (newZoom !== undefined) onZoomChange(newZoom);
  };

  const onMapTypeChange = ({ target }: { target: { getMapType: () => { getName: () => string } } }) => {
    if (mapRef && target?.getMapType) {
      if (mapType.current === BMAP_PERSPECTIVE_MAP?.getName?.()) {
        onZoomChange(DEFAULT_ZOOM_LEVEL);
      }
      const newMapType = target.getMapType();
      mapType.current = newMapType.getName();
    }
  };

  const clusters = formBaiduClusters({ zoom, points: markers });

  return (
    <div ref={container} style={{ height: mapHeight }}>
      <BaiduMap
        apiKey={BAIDU_MAP_API_KEY}
        getMapRef={(ref: MapRefs) => {
          if (!mapRef) setMapRef(ref);
          if (!isMapLoaded) setIsMapLoaded(true);
        }}
        center={center}
        zoom={zoom}
        style={{ height: '100%' }}
        mapProps={{
          onLoad: () => setIsMapLoaded(true),
          onDragStart: () => setOpenInfoWindow(false),
          onZoomStart: () => setOpenInfoWindow(false),
          onZoomEnd: event => onZoom(event),
          onMapTypeChange,
        }}
      >
        <FullScreenControl container={container} />
        {clusters?.map(({ latitude, longitude, count, subRegions = [], cluster, code, label }) => (
          <ClusterBubble
            position={{ lat: latitude, lng: longitude }}
            label={String(count)}
            showRing={!!cluster}
            onClick={event => {
              event?.stopPropagation?.();
              if (cluster) onClusterClick({ latitude, longitude, subRegions });
              else onMarkerClick({ code, label });
            }}
            onTouchEnd={event => {
              event?.stopPropagation?.();
              if (cluster) onClusterClick({ latitude, longitude, subRegions });
              else onMarkerClick({ code, label });
            }}
          />
        ))}
        <CustomOverlay
          key="info-window-label"
          position={{ lat: infoWindowData.lat, lng: infoWindowData.lng }}
          visiable={openInfoWindow}
          zIndex={500}
        >
          <InfoWindowContainer>
            <CloseInfoWindowButton onClick={() => setOpenInfoWindow(false)}>
              <Icon iconClass="icon-clear" />
            </CloseInfoWindowButton>
            <InfoWindowRegionList>
              {markerRegionData?.map((item, index: number) => (
                <div className={clsx(`link-container ${index === markerRegionData.length - 1 ? 'pb-0 mb-0' : ''}`)}>
                  <Link
                    linkClassName={clsx('t-font-xs', 'link-custom')}
                    linkHref={formatExitURL(item.regionCode, item.regionName, selectedRegion.code, isFiltersApplied)}
                    touchCallback={event => {
                      event?.stopPropagation?.();
                      onMarkerClick({ code: item.regionCode, label: item.regionName });
                    }}
                    target="_self"
                  >
                    {item?.regionName + ' (' + item?.count + ')'}
                  </Link>
                </div>
              ))}
            </InfoWindowRegionList>
          </InfoWindowContainer>
        </CustomOverlay>
      </BaiduMap>
    </div>
  );
};

export default BaiduMapClusters;
